import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { useCurrency } from "../../../context/CurrencyContext";

import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { OfferImpact, OfferStatus } from "../../../shared/CaseModel";
import { getCoveredQuantums } from "../../../shared/apportionmentEngine";
import { baselineNet, settledNet } from "../../../shared/getNetWalkaway";
import { NetResultChart, settlementImpactData } from "../NetResultChart";
import { Offer } from "./SettlementOfferDialog";
import { apportionedBy } from "./settlementModel";

const OfferSummary = ({
  offer,
  offeree,
  offeror,
  partyId,
}: {
  offer: Offer;
  offeror: OfferImpact;
  offeree: OfferImpact;
  // the party whose analytics page this offer is shown under; the takeaway is
  // narrated from its perspective
  partyId: string;
}) => {
  const { currency } = useCurrency();
  const { theme } = useTheme();

  const offerorAmount = offer.payment.offereePays
    ? offer.payment.amount
    : -offer.payment.amount;
  // the settlement range the offer is measured against, read from the side
  // that receives the payment; absent when the parties have no range at the
  // offer's stage, leaving nothing to compare the offer with
  const receiverEv = (offer.payment.offereePays ? offeror : offeree)
    .evAtStageOfOffer;
  const offerorEv = offeror.evAtStageOfOffer;
  const range = receiverEv &&
    offerorEv && {
      floor: receiverEv.ofParty,
      midpoint: Math.abs(offerorEv.mid),
      ceiling: receiverEv.ofOpponent,
    };

  const offerorNetBalance = settledNet(offeror) - baselineNet(offeror);
  const offereeNetBalance = settledNet(offeree) - baselineNet(offeree);

  const { t } = useTranslation(undefined, {
    keyPrefix: "case.analytics.settlement.offerSummary",
  });

  // How much of the settled claim(s) this settlement covers (face value) and
  // how much is therefore left to pursue against the remaining opponents.
  // Mirrors the apportionment waterfall the card already shows.
  const settledClaimTitles = offer.claims
    .filter((c) => c.settledByOffer)
    .map((c) => c.title);
  const coveredQuantums = getCoveredQuantums(offer.claims, offer.quantums);
  const waterfall = apportionedBy(offer);
  const claimTotalValue = coveredQuantums.reduce(
    (s, { value }) => s + value,
    0,
  );
  const claimCovered = coveredQuantums.reduce(
    (s, { id }) => s + (waterfall.quantums[id] ?? 0),
    0,
  );
  const claimRemaining = Math.max(0, claimTotalValue - claimCovered);

  // Risk profile: each party's whole-case outcome distribution if they accept
  // (settledStats) vs the case baseline (their current partyStats). Explains how
  // a slightly lower expected net result can still be a better deal — fewer
  // downside outcomes / a higher chance of a non-negative result. Only relevant
  // for a prospective (not-yet-accepted) offer.
  const riskCell = (
    from: number,
    to: number,
    fmt: (n: number) => string,
    lowerIsBetter = false,
  ): ReactNode => {
    const improved = lowerIsBetter ? to < from : to > from;
    const worsened = lowerIsBetter ? to > from : to < from;
    const color = improved
      ? theme.palette.data.positive.dark
      : worsened
        ? theme.palette.data.negative.dark
        : undefined;
    return (
      <TableCell align="right">
        <Typography
          variant="body2"
          component="span"
          sx={{
            color: "text.secondary",
          }}
        >
          {`${fmt(from)} → `}
        </Typography>
        <Typography variant="body2" component="span" sx={{ color: color }}>
          <b>{fmt(to)}</b>
        </Typography>
      </TableCell>
    );
  };

  const renderRiskChart = (): ReactNode => {
    // The offer is accepted by the OFFEREE, but the figures are shown from the
    // perspective of the CURRENT party (whose page this is) — offeror or offeree.
    const currentIsOfferee = offer.offeree.id === partyId;
    const meImpact = currentIsOfferee ? offeree : offeror;
    const meName = currentIsOfferee ? offer.offeree.name : offer.offeror.name;
    // Per-offer no-settlement world (live for draft/offered, frozen at the
    // decision for rejected/accepted) — stable, unlike the whole-case baseline
    // which after acceptance already bakes in the settlement.
    const base = meImpact.baselineStats;
    const settled = meImpact.settledStats;
    if (!base || !settled) return null;

    // The current party's net-result range (worst/avg/best) without vs with the
    // settlement, fed to the shared NetResultChart.
    const data = settlementImpactData(base.netResult, settled.netResult);
    if (!data) return null;

    return (
      <>
        <Typography
          variant="overline"
          sx={{ mt: theme.spacing(2), display: "block" }}
        >
          {t("risk.title", { name: meName })}
        </Typography>
        <Box sx={{ width: theme.spacing(64), height: theme.spacing(32) }}>
          <NetResultChart
            data={data}
            selectedPartyId="settled"
            onSelected={() => {}}
            compact
          />
        </Box>
      </>
    );
  };

  // Remaining opponents: the party's expected position against each opponent it
  // still litigates after this offer, no-settlement → settled. Makes the
  // cross-effect of a partial settlement (e.g. a jointly-claimed quantum shrinking
  // against the un-settled defendant) visible.
  const renderRemainingFor = (
    party: OfferImpact,
    partyName: string,
    counterpartName: string,
  ): ReactNode => {
    if (offer.status === OfferStatus.Accepted) return null;
    const remaining = party.remaining;
    if (!remaining || remaining.length === 0) return null;

    // Largest adverse shift in expected position, for the explanatory caption.
    const worstShift = [...remaining]
      .map((r) => ({ r, delta: r.settled.partyEv - r.baseline.partyEv }))
      .sort((a, b) => a.delta - b.delta)[0];
    const caption =
      worstShift && worstShift.delta < 0 && claimTotalValue > 0
        ? t("remaining.takeaway", {
            counterpart: counterpartName,
            opponent: worstShift.r.name,
            claim: settledClaimTitles.join(", "),
            covered: currency.format(claimCovered),
            total: currency.format(claimTotalValue),
            remaining: currency.format(claimRemaining),
          })
        : undefined;

    return (
      <>
        <Table
          key={`remaining-${partyName}`}
          size="small"
          aria-label={t("remaining.title", { name: partyName })}
          sx={{
            mt: theme.spacing(2),
            maxWidth: "fit-content",
            "& th, & td": { whiteSpace: "nowrap" },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell align="center">
                <b>{t("remaining.title", { name: partyName })}</b>
              </TableCell>
              <TableCell align="center">
                <b>{t("remaining.expected")}</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {remaining.map((r) => (
              <TableRow key={r.opponentId}>
                <TableCell align="center">{r.name}</TableCell>
                {riskCell(
                  r.baseline.partyEv,
                  r.settled.partyEv,
                  currency.format,
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {caption && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              mt: theme.spacing(1),
              display: "block",
              width: 0,
              minWidth: "100%",
            }}
          >
            {caption}
          </Typography>
        )}
      </>
    );
  };

  const renderRemaining = (): ReactNode => (
    <>
      {renderRemainingFor(offeror, offer.offeror.name, offer.offeree.name)}
      {renderRemainingFor(offeree, offer.offeree.name, offer.offeror.name)}
    </>
  );

  const signedDelta = (to: number) => {
    const delta = offer.payment.amount - to;
    return `${delta > 0 ? "+" : ""}${currency.format(delta)}`;
  };

  return (
    <Box>
      <TableContainer sx={{ width: "fit-content", maxWidth: "100%" }}>
        {range ? (
          <Table
            key="summary"
            size="small"
            aria-label={t("ev.label")}
            sx={{ maxWidth: "fit-content" }}
          >
            <TableHead>
              <TableRow>
                <TableCell align="center">
                  <b>
                    {t("ev.offer", {
                      amount: currency.format(offerorAmount),
                    })}
                  </b>
                </TableCell>
                <TableCell align="center">
                  <b>
                    {t("ev.floor", {
                      name: offer.payment.offereePays
                        ? offer.offeree.name
                        : offer.offeror.name,
                    })}
                  </b>
                </TableCell>
                <TableCell align="center">
                  <b>{t("ev.mid")}</b>
                </TableCell>
                <TableCell align="center">
                  <b>
                    {t("ev.ceiling", {
                      name: offer.payment.offereePays
                        ? offer.offeree.name
                        : offer.offeror.name,
                    })}
                  </b>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell align="center">{t("ev.withoutOffer")}</TableCell>
                <TableCell align="right">
                  {currency.format(range.floor)}
                </TableCell>
                <TableCell align="right">
                  {currency.format(range.midpoint)}
                </TableCell>
                <TableCell align="right">
                  {currency.format(range.ceiling)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="center">
                  <b>{t("ev.delta")}</b>
                </TableCell>
                <TableCell align="right">
                  <b>{signedDelta(range.floor)}</b>
                </TableCell>
                <TableCell align="right">
                  <b>{signedDelta(range.midpoint)}</b>
                </TableCell>
                <TableCell align="right">
                  <b>{signedDelta(range.ceiling)}</b>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            {t("ev.noRange")}
          </Typography>
        )}
        <Table
          key="netResult"
          size="small"
          aria-label={t("netResult.label")}
          sx={{ mt: theme.spacing(2), maxWidth: "fit-content" }}
        >
          <TableHead>
            <TableRow>
              <TableCell align="center" />
              <TableCell align="center">
                <b>{t("netResult.offeror", { name: offer.offeror.name })}</b>
              </TableCell>
              <TableCell align="center">
                <b>{t("netResult.offeree", { name: offer.offeree.name })}</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell align="center">{t("netResult.withOffer")}</TableCell>
              <TableCell align="right">
                {currency.format(settledNet(offeror))}
              </TableCell>
              <TableCell align="right">
                {currency.format(settledNet(offeree))}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="center">
                {t("netResult.withoutOffer")}
              </TableCell>
              <TableCell align="right">
                {currency.format(baselineNet(offeror))}
              </TableCell>
              <TableCell align="right">
                {currency.format(baselineNet(offeree))}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="center">
                <b>{t("netResult.delta")}</b>
              </TableCell>
              <TableCell align="right">
                <b>
                  {`${offerorNetBalance > 0 ? "+" : ""}${currency.format(offerorNetBalance)}`}
                </b>
              </TableCell>
              <TableCell align="right">
                <b>{`${offereeNetBalance > 0 ? "+" : ""}${currency.format(offereeNetBalance)}`}</b>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        {renderRiskChart()}
        {renderRemaining()}
      </TableContainer>
    </Box>
  );
};

export default OfferSummary;
